import { type RefObject, useCallback, useState } from "react";
import { Control, Controller, Path } from "react-hook-form";

import { MemberListItem } from "@components/TeamList/MemberListItem";
import { TeamSelector } from "@components/AddNewEntity";
import { BounceBar } from "@ui/Loaders";
import { FormLabel } from "@ui/FormElements";
import { useTimeoutRef } from "@hooks/useTimeoutRef";
import { User } from "@app-types/common.type";
import { DEBOUNCE_MS } from "@config/constants";

export interface IHasUsersField {
  users: User[];
}

interface ICoverageTeamFieldProps<T extends IHasUsersField> {
  control: Control<T>;
  options: any[];
  label: string;
  hideSelectAllOption?: boolean;
  onSave?: (value: { id: string }[]) => void;
  isLoading?: boolean;
  headerRef?: RefObject<HTMLElement>;
  footerRef?: RefObject<HTMLElement>;
}

export function TeamField<T extends IHasUsersField>({
  control,
  options,
  label,
  hideSelectAllOption = false,
  onSave,
  isLoading = false,
  headerRef,
  footerRef,
}: ICoverageTeamFieldProps<T>) {
  const [teamSearchValue, setTeamSearchValue] = useState<string>("");
  const { schedule: scheduleSave } = useTimeoutRef();

  const debouncedSave = useCallback(
    (members: { id: string }[]) => {
      scheduleSave(() => {
        onSave?.(members);
      }, DEBOUNCE_MS);
    },
    [onSave, scheduleSave]
  );

  return (
    <Controller
      key={options.length}
      control={control}
      name={"users" as Path<T>}
      render={({ field: { value, onChange } }) => {
        const membersArray: User[] = Array.isArray(value)
          ? value.filter((item): item is User => !!item)
          : [];

        return (
          <div className="flex flex-col gap-1.5 mb-2">
            <FormLabel htmlFor="user" className="!mb-0">
              {label}
            </FormLabel>
            <div className="w-full">
              <TeamSelector
                placeholder="Add team members"
                value={membersArray}
                searchValue={teamSearchValue}
                setSearchValue={setTeamSearchValue}
                options={options ?? []}
                onChange={(members) => {
                  const filteredMembers = members.filter(
                    (member) => member.id !== "selectAllOption"
                  );
                  onChange(filteredMembers);
                  debouncedSave(filteredMembers);
                }}
                hideSelectAllOption={hideSelectAllOption}
                headerRef={headerRef}
                footerRef={footerRef}
              />
            </div>
            <div className="flex flex-col gap-1">
              {isLoading && !membersArray.length
                ? Array.from({ length: 3 }, (_, i) => (
                    <div key={i} className="flex items-center gap-2 px-2 min-h-[36px]">
                      <BounceBar className="h-5 w-5 !rounded-full shrink-0 bg-gray-200 dark:bg-gray-600" />
                      <BounceBar className="h-4 w-24 rounded-2xl bg-gray-200 dark:bg-gray-600" />
                    </div>
                  ))
                : value &&
                  membersArray.map((member: any, index: number) => {
                    return (
                      <MemberListItem
                        erasable
                        key={member.id}
                        member={member}
                        onDelete={() => {
                          const updated = membersArray.filter((_, i) => i !== index);
                          onChange(updated);
                          debouncedSave(updated);
                        }}
                      />
                    );
                  })}
            </div>
          </div>
        );
      }}
    />
  );
}
